import { ERROR_KIND } from './contract';

/**
 * Single error type for every failed request, whether it came from the server
 * as a 4xx or 5xx response or from the transport layer as a network failure,
 * a timeout, or a cancellation.
 *
 * The shape is identical regardless of the source, so callers never need to
 * check whether `error.response` exists before reading a message.
 */
export default class ApiError extends Error {
  /**
   * @param {Object} params - Error details.
   * @param {number} params.code - Contract code (200, 400, 401, ..., 500).
   * @param {string} params.message - Message ready to show in an alert.
   * @param {Object<string, string[]>|null} [params.errors] - Per-field errors.
   * @param {*} [params.data] - Extra payload when the server sends one.
   * @param {string|null} [params.detail] - Technical note, never shown to users.
   * @param {string|null} [params.requestId] - Correlation ID from the server.
   * @param {string} params.kind - One of the {@link ERROR_KIND} values.
   * @param {*} [params.legacyCode] - Original code when the response was legacy.
   * @param {Error} [params.cause] - Underlying error.
   */
  constructor({
    code,
    message,
    errors = null,
    data = null,
    detail = null,
    requestId = null,
    kind,
    legacyCode = null,
    cause = null,
  }) {
    super(message);

    /**
     * Babel transpiles `extends Error` in a way that breaks the prototype
     * chain, which makes `instanceof ApiError` return false. This restores it.
     */
    Object.setPrototypeOf(this, ApiError.prototype);

    this.name = 'ApiError';
    this.code = code;
    this.errors = errors;
    this.data = data;
    this.detail = detail;
    this.requestId = requestId;
    this.kind = kind;
    this.legacyCode = legacyCode;
    this.cause = cause;

    /**
     * Set to true once a global handler has dealt with this error, for example
     * a 401 that already triggered a sign-out. Module code checks this flag to
     * avoid showing a second alert for the same failure.
     *
     * @type {boolean}
     */
    this.handled = false;

    /**
     * Keeps the response shape that `beforeSend` in plugins/sentry.js filters
     * on. Without it every 4xx error would reach Sentry, because that filter
     * only drops errors whose `response.status` is present and not 500.
     *
     * @type {{ status: number, data: * }}
     */
    this.response = { status: code, data };

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  /**
   * @returns {boolean} True when field validation failed (422).
   */
  get isValidation() {
    return this.kind === ERROR_KIND.VALIDATION;
  }

  /**
   * @returns {boolean} True when the session is invalid (401).
   */
  get isUnauthenticated() {
    return this.kind === ERROR_KIND.UNAUTHENTICATED;
  }

  /**
   * @returns {boolean} True when access was denied (403).
   */
  get isForbidden() {
    return this.kind === ERROR_KIND.FORBIDDEN;
  }

  /**
   * @returns {boolean} True when the resource state conflicts (409).
   */
  get isConflict() {
    return this.kind === ERROR_KIND.CONFLICT;
  }

  /**
   * @returns {boolean} True when the server failed internally (5xx).
   */
  get isServer() {
    return this.kind === ERROR_KIND.SERVER;
  }

  /**
   * @returns {boolean} True when the caller canceled the request, which is not
   *   a real failure and should not surface to the user.
   */
  get isCanceled() {
    return this.kind === ERROR_KIND.CANCELED;
  }

  /**
   * @returns {boolean} True when the request never reached the server.
   */
  get isNetwork() {
    return this.kind === ERROR_KIND.NETWORK || this.kind === ERROR_KIND.TIMEOUT;
  }

  /**
   * Returns every message recorded for one form field.
   *
   * @param {string} field - Field name as sent in the `errors` object.
   * @returns {string[]} Messages, empty when the field has none.
   */
  fieldErrors(field) {
    return this.errors?.[field] ?? [];
  }

  /**
   * Returns the first message recorded for one form field.
   *
   * @param {string} field - Field name as sent in the `errors` object.
   * @returns {string|null} The first message, or null when the field has none.
   */
  firstFieldError(field) {
    return this.fieldErrors(field)[0] ?? null;
  }

  /**
   * Message for alerts. Server errors include the correlation ID so users can
   * quote it when they raise a support ticket.
   *
   * @returns {string} Message ready to display.
   */
  get alertMessage() {
    if (this.isServer && this.requestId) {
      return `${this.message} (Ref: ${this.requestId})`;
    }
    return this.message;
  }
}

/**
 * Type guard for {@link ApiError}.
 *
 * @param {*} value - Value to test.
 * @returns {boolean} True when the value is an ApiError.
 */
export const isApiError = (value) => value instanceof ApiError;

/**
 * Builds an {@link ApiError} for failures that produced no server response.
 *
 * The code is zero because no HTTP status was ever received. Reporting these as
 * 500 would let a dropped connection be mistaken for a server fault, both by
 * modules that branch on `code` and by error reporting. Use `isNetwork` or
 * `kind` to recognise them.
 *
 * @param {string} kind - One of the {@link ERROR_KIND} values.
 * @param {string} message - Message ready to display.
 * @param {Error} [cause] - Underlying error.
 * @returns {ApiError} The wrapped transport failure.
 */
export const transportError = (kind, message, cause = null) =>
  new ApiError({
    code: 0,
    message,
    kind,
    cause,
  });
